import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MODULES } from "@/portal/modulesData";
import { Check, ArrowRight, ArrowLeft, PlayCircle } from "lucide-react";

const ASSET_LINKS = {
  "Asset 02": "/portal/assets?open=market-list",
  "Asset 03": "/portal/assets?open=research-prompts",
  "Asset 04": "/portal/assets?open=positioning",
  "Asset 05": "/portal/assets?open=hiring",
  "Asset 08": "/portal/scripts",
};

// Turn in-text "Asset 0X" references into clickable deep links to the right page.
function linkifyAssets(text) {
  const parts = text.split(/(Asset 0[2-8])/g);
  return parts.map((part, i) => {
    const to = ASSET_LINKS[part];
    if (to) {
      return (
        <Link key={i} to={to} data-testid={`asset-link-${part.replace(" ", "-").toLowerCase()}`}
          style={{ color: "#2038E6", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 2 }}>
          {part}
        </Link>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export default function LessonPage() {
  const { moduleId } = useParams();
  const id = parseInt(moduleId, 10);
  const mod = MODULES.find((m) => m.id === id);
  const { progress, refreshProgress } = useAuth();
  const nav = useNavigate();
  const done = new Set(progress?.modules_completed || []);
  const isDone = done.has(id);

  if (!mod) return <div data-testid="lesson-notfound">Module not found. <Link to="/portal/training" style={{ color: "#2038E6" }}>Back to training</Link></div>;

  const completeAndContinue = async () => {
    if (!isDone) { await api.post("/progress/module", { module_id: id, completed: true }); await refreshProgress(); }
    const next = MODULES.find((m) => m.id === id + 1);
    nav(next ? `/portal/training/${next.id}` : "/portal/training");
  };

  return (
    <div data-testid={`lesson-page-${id}`}>
      <Link to="/portal/training" style={{ color: "var(--muted)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
        <ArrowLeft size={15} /> The Training
      </Link>
      <div className="eyebrow" style={{ color: "#2038E6", marginTop: 18 }}>Module {id}</div>
      <h1 className="h-display" style={{ fontSize: "clamp(30px,4vw,44px)", marginTop: 8 }}>{mod.title}</h1>

      {/* Video embed slot */}
      <div data-testid="video-slot" style={{ marginTop: 22, borderRadius: 14, overflow: "hidden", border: "1px solid var(--line)" }}>
        {mod.videoUrl ? (
          <div style={{ position: "relative", paddingTop: "56.25%" }}>
            <iframe title={`module-${id}`} src={mod.videoUrl} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} allowFullScreen />
          </div>
        ) : (
          <div style={{ aspectRatio: "16/9", background: "#0F1317", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "#E9ECE7", textAlign: "center", padding: 24 }}>
            <div style={{ position: "absolute", top: 18, left: 18, width: 40, height: 6, background: "#FFDF57" }} />
            <PlayCircle size={46} color="#2038E6" strokeWidth={1.5} />
            <span className="font-mono-plex" style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "#2038E6" }}>Module {id} Walkthrough</span>
            <span className="font-archivo" style={{ fontWeight: 800, fontSize: 20 }}>Video coming soon</span>
            <span style={{ fontSize: 13, color: "#9AA2AA", maxWidth: 380 }}>The full written lesson is ready below. Work through it now; the video walkthrough drops into this slot the moment it's recorded.</span>
          </div>
        )}
      </div>

      {/* Lesson content (verbatim from 01-core-training.md) */}
      <div className="prose-weekend" style={{ marginTop: 26 }}>
        <div style={{ background: "#fff", border: "1px solid var(--line)", borderLeft: "4px solid #2038E6", borderRadius: 12, padding: "18px 20px" }}>
          <span className="eyebrow" style={{ color: "#2038E6" }}>Big idea</span>
          <p style={{ fontFamily: "Archivo, sans-serif", fontWeight: 700, fontSize: 20, lineHeight: 1.3, marginTop: 6 }} data-testid="lesson-big-idea">{mod.bigIdea}</p>
        </div>

        <h3 className="font-archivo" style={{ fontWeight: 800, fontSize: 18, marginTop: 28, marginBottom: 4 }}>Talking points</h3>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }} data-testid="lesson-talking-points">
          {mod.talkingPoints.map((pt, i) => (
            <li key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
              <span className="font-mono-plex" style={{ color: "#2038E6", fontSize: 13, flexShrink: 0, marginTop: 3 }}>{String(i + 1).padStart(2, "0")}</span>
              <span style={{ color: "var(--ink)" }}>{linkifyAssets(pt)}</span>
            </li>
          ))}
        </ul>

        <div style={{ marginTop: 24, background: "#FFFBEA", border: "1px solid #F0E4A8", borderRadius: 12, padding: "18px 20px" }}>
          <span className="eyebrow" style={{ color: "#8A6D00" }}>Homework</span>
          <p style={{ marginTop: 6 }} data-testid="lesson-homework">{linkifyAssets(mod.homework)}</p>
        </div>
      </div>

      <div style={{ marginTop: 34, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn-cobalt" onClick={completeAndContinue} data-testid="mark-complete-continue">
          {isDone ? "Continue" : "Mark complete and continue"} <ArrowRight size={16} />
        </button>
        {isDone && <span className="font-mono-plex" style={{ fontSize: 12, color: "#2038E6", display: "inline-flex", alignItems: "center", gap: 6 }}><Check size={15} /> Completed</span>}
      </div>
    </div>
  );
}
